import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import { api, type ProtectionSettings } from '@/api/client'
import { SecurityDepositAutomationCard } from '@/components/settings/security-deposit-automation-card'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { LoadingSkeleton } from '@/components/ui/loading-skeleton'
import { ErrorState } from '@/components/ui/error-state'
import { useToast } from '@/hooks/use-toast'
import { cn } from '@/lib/utils'
import { formatDepositAmount } from './deposit-presentation'

/** Mirrors admin-api's MIN/MAX_DEPOSIT_AMOUNT_CENTS, so a bad amount is caught here. */
const MIN_DOLLARS = 1
const MAX_DOLLARS = 10_000

function hoursAsWords(hours: number): string {
  if (hours >= 48 && hours % 24 === 0) {
    const days = hours / 24
    return `${days} day${days === 1 ? '' : 's'}`
  }
  return `${hours} hours`
}

/**
 * How deposits behave, for every listing and per listing, modeled on
 * Uplisting's Protection settings.
 *
 * Only the things with a backend switch behind them are controls: the kill
 * switch, each listing's on/off, and each listing's amount. The timing rules
 * and channels are fixed in code, so they are stated rather than offered as
 * toggles that would do nothing.
 */
export function SettingsTab() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['protection-settings'],
    queryFn: ({ signal }) => api.getProtectionSettings(signal),
    staleTime: 30_000,
  })

  return (
    <div className="space-y-6">
      {/* Fetches its own state, so the emergency stop is usable even if the
          settings read below is slow or failing. */}
      <SecurityDepositAutomationCard />

      {error && !data ? (
        <Card>
          <CardContent className="pt-6">
            <ErrorState error={error as Error} onRetry={refetch} />
          </CardContent>
        </Card>
      ) : isLoading || !data ? (
        <Card>
          <CardContent className="pt-6">
            <LoadingSkeleton className="h-40 w-full" />
          </CardContent>
        </Card>
      ) : (
        <>
          <RulesCard settings={data} />
          <ListingsCard settings={data} />
        </>
      )}
    </div>
  )
}

function RulesCard({ settings }: { settings: ProtectionSettings }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>How deposits work</CardTitle>
        <CardDescription>
          The same rules for every listing. They are set in code, so they are shown here rather than
          edited.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <dl className="grid gap-4 sm:grid-cols-2">
          <Rule label="Default amount">
            {formatDepositAmount(settings.default_amount_cents)}, for any listing without its own
            amount below.
          </Rule>
          <Rule label="When the hold goes on">
            {hoursAsWords(settings.placement_lead_hours)} before the guest&apos;s arrival time.
          </Rule>
          <Rule label="Renewals">
            Renewed before it expires, until it covers{' '}
            {hoursAsWords(settings.coverage_after_checkout_hours)} after checkout.
          </Rule>
          <Rule label="Releasing a hold">
            Never released early. Once the hold covers{' '}
            {hoursAsWords(settings.coverage_after_checkout_hours)} after checkout it is left to run
            out on its own, so it can still be captured if damage turns up late. It is released{' '}
            {hoursAsWords(settings.max_hold_after_checkout_hours)} after checkout at the latest, and
            straight away if the booking is cancelled.
          </Rule>
        </dl>

        <div>
          <h3 className="mb-2 text-sm font-semibold text-text">Channels</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Channel</TableHead>
                <TableHead>Security deposits</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {settings.channels.map(channel => (
                <TableRow key={channel.channel}>
                  <TableCell className="text-sm">
                    {/* The API's label: it covers rows like "Booking.com and any other
                        channel" that have no single app-wide channel name. */}
                    <div className="font-medium text-text">{channel.label}</div>
                    {channel.note && <div className="text-xs text-textMuted">{channel.note}</div>}
                  </TableCell>
                  <TableCell>
                    <Badge variant={channel.enabled ? 'success' : 'secondary'}>
                      {channel.enabled ? 'On' : 'Off'}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )
}

function Rule({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-sm font-medium text-text">{label}</dt>
      <dd className="mt-1 text-sm text-textMuted">{children}</dd>
    </div>
  )
}

function ListingsCard({ settings }: { settings: ProtectionSettings }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Listing by listing</CardTitle>
        <CardDescription>
          Turn deposits on or off for a listing, or give it its own amount. Changes apply from the
          next hourly sweep. A hold already on a card keeps the amount it was placed at.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Listing</TableHead>
              <TableHead>Deposit</TableHead>
              <TableHead>Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {settings.listings.map(listing => (
              <ListingRow
                key={listing.property_id}
                listing={listing}
                defaultCents={settings.default_amount_cents}
              />
            ))}
          </TableBody>
        </Table>
        <p className="mt-4 text-sm text-textMuted">
          {settings.summary.enabled_count} of {settings.summary.total} listings ask for a deposit.
        </p>
      </CardContent>
    </Card>
  )
}

type Listing = ProtectionSettings['listings'][number]

function ListingRow({ listing, defaultCents }: { listing: Listing; defaultCents: number }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const name = listing.name || listing.property_id
  const savedDollars = String(listing.resolved_amount_cents / 100)
  const [amount, setAmount] = useState(savedDollars)
  const [seededFrom, setSeededFrom] = useState(savedDollars)

  // Follow the server after a save or a refetch, without clobbering an edit
  // in progress: only reseed when the saved value itself changed. Adjusted
  // during render (React's pattern for state derived from a prop), not in an
  // effect, so there is no extra render with the stale value.
  if (seededFrom !== savedDollars) {
    setSeededFrom(savedDollars)
    setAmount(savedDollars)
  }

  const invalidate = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['protection-settings'] }),
      queryClient.invalidateQueries({ queryKey: ['protection-deposits'] }),
      queryClient.invalidateQueries({ queryKey: ['property-features', listing.property_id] }),
      queryClient.invalidateQueries({
        queryKey: ['security-deposit-config', listing.property_id],
      }),
    ])

  const toggle = useMutation({
    mutationFn: (enabled: boolean) =>
      api.setPropertyFeature(listing.property_id, 'security_deposit', enabled),
    onSuccess: async (_data, enabled) => {
      await invalidate()
      toast({
        title: enabled ? 'Deposits on' : 'Deposits off',
        description: `${name}: ${enabled ? 'a deposit will be held on every booking except Airbnb.' : 'no new deposits. A hold already placed is released.'}`,
      })
    },
    onError: (err: unknown) =>
      toast({
        title: `Couldn't change ${name}`,
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      }),
  })

  const dollars = Number(amount)
  const amountValid =
    amount.trim() !== '' &&
    Number.isFinite(dollars) &&
    dollars >= MIN_DOLLARS &&
    dollars <= MAX_DOLLARS
  // Compared as money, not text: "750.00" against a saved 750 is not a change.
  const dirty = !(amountValid && Math.round(dollars * 100) === listing.resolved_amount_cents)

  const save = useMutation({
    mutationFn: () => {
      const cents = Math.round(dollars * 100)
      // Entering exactly the default clears the override, so the listing keeps
      // following the default instead of freezing today's value.
      return api.updateSecurityDepositConfig(
        listing.property_id,
        cents === defaultCents ? null : cents
      )
    },
    onSuccess: async () => {
      await invalidate()
      toast({
        title: 'Amount saved',
        description: `${name}: ${formatDepositAmount(dollars * 100)}.`,
      })
    },
    onError: (err: unknown) =>
      toast({
        title: `Couldn't save ${name}`,
        description: err instanceof Error ? err.message : 'Please try again.',
        variant: 'destructive',
      }),
  })

  return (
    <TableRow>
      <TableCell className="text-sm font-medium text-text">{name}</TableCell>
      <TableCell>
        <button
          type="button"
          role="switch"
          aria-checked={listing.enabled}
          aria-label={`Security deposit for ${name}`}
          disabled={toggle.isPending}
          onClick={() => toggle.mutate(!listing.enabled)}
          className={cn(
            'relative inline-flex h-8 w-[46px] shrink-0 cursor-pointer rounded-full border-2 border-transparent p-0.5 transition-colors',
            'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
            'disabled:cursor-not-allowed disabled:opacity-50',
            listing.enabled ? 'bg-mint' : 'bg-surface2'
          )}
        >
          <span
            aria-hidden
            className={cn(
              'pointer-events-none inline-block size-6 rounded-full bg-white shadow-xs transition-[transform]',
              listing.enabled ? 'translate-x-[18px]' : 'translate-x-0'
            )}
          />
          {toggle.isPending && (
            <span className="absolute inset-0 flex items-center justify-center rounded-full bg-surface2/70">
              <Loader2 className="size-4 animate-spin text-mint" aria-hidden />
            </span>
          )}
        </button>
      </TableCell>
      <TableCell>
        <form
          className="flex items-center gap-2"
          onSubmit={e => {
            e.preventDefault()
            if (amountValid && dirty) save.mutate()
          }}
        >
          <div className="relative w-28">
            <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-textMuted">
              $
            </span>
            <Input
              inputMode="decimal"
              value={amount}
              onChange={e => setAmount(e.target.value)}
              aria-label={`Deposit amount for ${name}, in dollars`}
              aria-invalid={!amountValid}
              className="h-9 pl-6"
            />
          </div>
          {dirty && (
            <Button type="submit" size="sm" disabled={!amountValid || save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          )}
          {listing.amount_cents == null && !dirty && (
            <span className="text-xs text-textMuted">Default</span>
          )}
        </form>
        {dirty && !amountValid && (
          <p className="mt-1 text-xs text-danger">
            Enter an amount between ${MIN_DOLLARS} and ${MAX_DOLLARS.toLocaleString('en-US')}.
          </p>
        )}
      </TableCell>
    </TableRow>
  )
}
